import React from 'react';
import PropTypes from 'prop-types';
import clone from 'clone';

import {oeInterfaceManager} from '../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../react-oe/oe-interface-adapter';
import {CustomScrollbarTrackVisibility} from '../../../lib/custom-scrollbar/custom-scrollbar';
import {OEVHLayouter} from '../../../lib/components/oe-vh-layouter';
import OEToolbox from '../../../lib/tools/oe-toolbox';
import OEResizeObserver from '../../../lib/components/oe-resize-observer';
import retardUpdate from '../../../lib/react/update-retarder';
import {OEBottomWidgetToolbarWidgetType, OEBottomWidgetToolbarLayoutType, OEBottomWidgetToolbarConfigFactory, OEBottomWidgetToolbarPropTypes} from './oe-bottom-widget-toolbar-model';
import OEMediaCenterItemToolbar from './oe-media-center-item-toolbar';
import OEPresentationSlideToolbar from './oe-presentation-slide-toolbar';
import {OEPresentationSlideMagnificationPlacement} from '../../controller/presentation/oe-presentation-slide-magnification';

export default class OEBottomWidgetToolbar extends React.PureComponent {
    
    constructor(props) {
        super(props);

        this.mounted = false;
        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.edgeOffsets = this.determineEdgeOffsets();
        this.initialUpdateVisibility = true;

        this.widgets = this.createWidgetArray();
        this.visible = this.shouldBeVisible();
        this.allowedLayoutTypes = this.determineAllowedLayoutTypes();

        this.layoutType = this.allowedLayoutTypes.length ? this.allowedLayoutTypes[0] : OEBottomWidgetToolbarLayoutType.std;

        this.state = {
            widgets: [...this.widgets],
            visible: this.visible,
            animateVisibility: false,
            allowedLayoutTypes: this.allowedLayoutTypes,
            layoutType: this.layoutType
        };

        this.onShouldBeVisibleChanged = this.onShouldBeVisibleChanged.bind(this);

        this.onLayoutTypeChanged = this.onLayoutTypeChanged.bind(this);
        this.onResize = this.onResize.bind(this);

        this.onRef = this.onRef.bind(this);
        this.onWidgetRef = this.onWidgetRef.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        if(!OEToolbox.shallowEqual(nextProps.config, this.props.config, true))     {
            if(!OEToolbox.shallowEqual(nextProps.config.widgets, this.props.config.widgets, true))     this.updateWidgets(nextProps);
            if(!OEToolbox.shallowEqual(nextProps.config.allowedLayoutTypes, this.props.config.allowedLayoutTypes))     this.updateAllowedLayoutTypes(nextProps);
            this.updateVisibility(nextProps);
        }
    }

    componentDidMount()    {
        this.mounted = true;

        retardUpdate(this, () => {
            this.updateEdgeOffsets();
        });
    }

    componentWillUnmount()    {
        this.mounted = false;
        this.updateEdgeOffsets();
    }

    onConnect() {}

    onRelease() {}

    determineEdgeOffsets()  {
        let edgeOffsets = {top: 0, right: 0, bottom: 0, left: 0};

        if(this.mounted && this.visible && this.size)   {
            if(this.layoutType === OEBottomWidgetToolbarLayoutType.horizontal) {
                edgeOffsets.bottom = this.size.h;
            } else {
                edgeOffsets.right = this.size.w;
            }
        }

        return edgeOffsets;
    }

    createWidgetArray(props)    {
        props = props || this.props;
        let this_ = this;
        let widgets = !props.config.widgets ? [] :
            props.config.widgets.map((w) => {
                let widget = clone(w);
                widget.onRef = (ref) => {
                    this_.onWidgetRef(ref, widget);
                };
                return widget;
            });
        return widgets;
    }
    
    shouldBeVisible(props) {
        props = props || this.props;
        if(!props.config.enabled)    return false;

        for(let i = 0; i < this.widgets.length; ++i)    {
            let widget = this.widgets[i];
            if(widget.ref && (typeof(widget.ref.shouldBeVisible) !== 'function' || widget.ref.shouldBeVisible()))   return true;
        }

        return false;
    }

    determineAllowedLayoutTypes(props)  {
        props = props || this.props;

        let allowedLayoutTypes = Array.isArray(props.config.allowedLayoutTypes) ? props.config.allowedLayoutTypes : OEBottomWidgetToolbarLayoutType.all;

        let ret = [];

        for(let i = 0; i < this.widgets.length; ++i)    {
            let widget = this.widgets[i];
            if(!widget.ref)  continue;

            let types;
            if(Array.isArray(widget.ref.allowedLayoutTypes)) {
                types = widget.ref.allowedLayoutTypes;
            } else if(typeof(widget.ref.allowedLayoutTypes) === 'string') {
                types = [widget.ref.allowedLayoutTypes];
            } else if(typeof(widget.ref.allowedLayoutTypes) === 'function')   {
                let types_ = widget.ref.allowedLayoutTypes();
                if(typeof(types_) === 'string') types_ = [types_];
                types = Array.isArray(types_) ? types_ : [];
            } else {
                types = [OEBottomWidgetToolbarLayoutType.std];
            }

            ret = !i ? types : ret.filter(type => types.includes(type));
        }

        ret.filter(layout => allowedLayoutTypes.includes(layout));

        if(!ret.length) ret = [OEBottomWidgetToolbarLayoutType.std];

        ret.sort();
        return ret;
    }

    updateEdgeOffsets(animated) {
        let edgeOffsets = this.determineEdgeOffsets();
        if(OEToolbox.shallowEqual(edgeOffsets, this.edgeOffsets))   return;

        this.edgeOffsets = edgeOffsets;
        if(this.props.onEdgeOffsetsChanged) this.props.onEdgeOffsetsChanged(edgeOffsets, !!animated);
    }

    updateWidgets(props)  {
        this.widgets = this.createWidgetArray(props);
        this.setState({widgets: [...this.widgets]});
    }

    updateVisibility(props, animated = true)  {
        let shouldBeVisible = this.shouldBeVisible(props);
        if(this.visible == shouldBeVisible) return;
        this.visible = shouldBeVisible;

        this.setState({
            visible: this.visible,
            animateVisibility: this.initialUpdateVisibility ? false : animated
        });

        this.updateEdgeOffsets(this.initialUpdateVisibility ? false : animated);
        
        this.initialUpdateVisibility = false;
    }

    updateAllowedLayoutTypes(props)  {
        let allowedTypes = this.determineAllowedLayoutTypes(props);
        if(OEToolbox.arrayShallowEqual(this.allowedLayoutTypes, allowedTypes))  return;     // this comparison makes sense since the arrays are sorted

        this.allowedLayoutTypes = allowedTypes;
        this.setState({allowedLayoutTypes: this.allowedLayoutTypes});
    }

    updateState(released)   {
        if(!this.oe.isReady() || released === true)   {
            return;
        }

        retardUpdate(this, () => {
            this.updateVisibility();
        });
    }

    onShouldBeVisibleChanged()  {
        this.updateVisibility();
    }

    onLayoutTypeChanged(sender, layoutType) {
        if(this.layoutType === layoutType) return;
        this.layoutType = layoutType;

        this.setState({
            layoutType: this.layoutType,
            animateVisibility: false
        });

        this.updateEdgeOffsets(false);
    }

    onResize(sender, size)   {
        this.size = size;
        this.updateEdgeOffsets(false);
    }

    onRef(ref)    {
        this.ref = ref;
    }

    onWidgetRef(ref, widget)   {
        if(widget.ref === ref)  return;
        widget.ref = ref;
        this.updateVisibility();
        this.updateAllowedLayoutTypes();
    }

    renderWidgets() {
        if(!this.state.widgets.length)  return null;
        let layoutClass = this.state.layoutType === OEBottomWidgetToolbarLayoutType.horizontal ? 'horizontal-layout' : 'vertical-layout';

        return this.state.widgets.map((w, i) => {
            let className = 'bottom-widget-toolbar-widget ' + layoutClass;
            if(i === this.state.widgets.length - 1) className += ' last';

            if(w.type === OEBottomWidgetToolbarWidgetType.presentationSlide)    {
                return (
                    <OEPresentationSlideToolbar
                        key={i}
                        appComponent={this.props.appComponent}
                        moduleId={this.props.moduleId}
                        className={className}
                        {...w.props}
                        visible={this.state.visible}
                        onShouldBeVisibleChanged={this.onShouldBeVisibleChanged}
                        ref={w.onRef}
                    />
                );
            } else if(w.type === OEBottomWidgetToolbarWidgetType.mediaCenterItem)    {

                const magnificationPlacement= this.state.layoutType === OEBottomWidgetToolbarLayoutType.horizontal ? OEPresentationSlideMagnificationPlacement.top : OEPresentationSlideMagnificationPlacement.left;

                const scrollbar = this.state.layoutType === OEBottomWidgetToolbarLayoutType.horizontal ?
                    {
                        horizontalTrackVisiblity:   CustomScrollbarTrackVisibility.always,
                        verticalTrackVisiblity:     CustomScrollbarTrackVisibility.none,
                    } : {

                        horizontalTrackVisiblity:   CustomScrollbarTrackVisibility.none,
                        verticalTrackVisiblity:     CustomScrollbarTrackVisibility.always,
                    };

                return (
                    <OEMediaCenterItemToolbar
                        key={i}
                        appComponent={this.props.appComponent}
                        moduleId={this.props.moduleId}
                        className={className}
                        innerClassName={layoutClass}
                        {...w.props}
                        visible={this.state.visible}
                        magnificationPlacement={magnificationPlacement}
                        scrollbar={scrollbar}
                        onShouldBeVisibleChanged={this.onShouldBeVisibleChanged}
                        ref={w.onRef}
                    />
                );
            } else {
                return <div className={className}/>;
            }
        });
    }

    render() {
        let className = 'bottom-bar bottom-widget-toolbar light-bg std-label-text-color';
        let style;

        if(this.state.layoutType === OEBottomWidgetToolbarLayoutType.horizontal)   {
            className += ' horizontal-layout';
            style = {
                left: this.props.insets.left.toString() + 'px',
                transform: 'translateY(' + (this.state.visible ? '0px' : '100%') + ')',
                transition: this.state.animateVisibility ? 'transform 0.333s ease' : ''
            }

            if(this.props.animate)  {
                if(style.transition)    style.transition += ', ';
                style.transition += 'left 0.333s ease';
            }

        } else {
            className += ' vertical-layout';
            style = {
                transform: 'translateX(' + (this.state.visible ? '0px' : '100%') + ')',
                transition: this.state.animateVisibility ? 'transform 0.333s ease' : ''
            }
        }

        return (
            <React.Fragment>
                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>
                
                <OEVHLayouter
                    allowedLayoutTypes={this.state.allowedLayoutTypes}
                    onLayoutTypeChanged={this.onLayoutTypeChanged}
                />

                <div 
                    className={className}
                    style={style}
                    ref={this.onRef}
                >
                    <OEResizeObserver onResize={this.onResize}/>
                    {this.renderWidgets()}
                </div>
            </React.Fragment>
        );
    }
};

OEBottomWidgetToolbar.defaultProps = {
    moduleId: '',
    insets: {left: 0},
    animate: false,
    config: (new OEBottomWidgetToolbarConfigFactory).build()
};

OEBottomWidgetToolbar.propTypes = {
    moduleId: PropTypes.string,
    insets: PropTypes.object,
    animate: PropTypes.bool,
    config: OEBottomWidgetToolbarPropTypes.config,
    onEdgeOffsetsChanged: PropTypes.func
};